import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BookOpen, MessageCircle, PenTool, FileText, Users, 
  Library, GraduationCap, HelpCircle, ChevronLeft, 
  Sparkles, Check, ArrowRight, Lightbulb, Target, Star
} from "lucide-react";

const UserManualPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("getting-started");

  const sections = [
    {
      id: "getting-started",
      title: "Premiers pas",
      icon: Sparkles,
      color: "text-[#D4AF37]"
    },
    {
      id: "modules",
      title: "Modules",
      icon: BookOpen,
      color: "text-[#1A365D]"
    },
    {
      id: "chat",
      title: "Chat & Debat",
      icon: MessageCircle,
      color: "text-[#3B82F6]"
    },
    {
      id: "exercises",
      title: "Exercices",
      icon: PenTool,
      color: "text-[#22C55E]"
    },
    {
      id: "tips",
      title: "Conseils Bac",
      icon: Lightbulb,
      color: "text-[#F59E0B]"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="user-manual-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#1A365D]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Mode d'emploi</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-5xl mx-auto page-enter">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Mode d'emploi SophIA
          </h1>
          <p className="text-[#64748B]">
            Tout ce que vous devez savoir pour bien utiliser votre professeur de philosophie virtuel
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent h-auto mb-8">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <TabsTrigger
                  key={section.id}
                  value={section.id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all data-[state=active]:bg-[#1A365D] data-[state=active]:text-white data-[state=active]:border-[#1A365D] data-[state=inactive]:border-[#E2E8F0] data-[state=inactive]:bg-white`}
                >
                  <Icon className={`w-4 h-4 ${activeTab === section.id ? 'text-white' : section.color}`} />
                  <span className="hidden sm:inline">{section.title}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Getting Started */}
          <TabsContent value="getting-started">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                  Bienvenue sur SophIA !
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-[#64748B] leading-relaxed">
                  SophIA est votre professeur de philosophie virtuel, disponible 24h/24 pour vous aider a preparer le Baccalaureat. 
                  Voici comment tirer le meilleur parti de cette application.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#F8F9FA] rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#22C55E]" />
                      Ce que SophIA peut faire
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Repondre a vos questions sur les 17 notions du programme
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Corriger vos dissertations et commentaires
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Organiser des debats entre philosophes
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Vous faire reviser avec des quiz
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-[#D4AF37]/10 rounded-lg">
                    <h3 className="font-semibold text-[#1A365D] mb-2 flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#D4AF37]" />
                      Systeme de progression
                    </h3>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Gagnez des points a chaque exercice
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Montez de niveau en accumulant des points
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Debloquez des badges speciaux
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        Comparez-vous au classement de votre classe
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-[#1A365D]/5 rounded-lg border border-[#1A365D]/20">
                  <h3 className="font-semibold text-[#1A365D] mb-2">
                    21 philosophes a votre service
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    De Socrate a Simone Weil, en passant par Descartes, Kant, Nietzsche et bien d'autres... 
                    Chaque philosophe a sa personnalite unique et peut vous expliquer ses idees avec son propre style.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules */}
          <TabsContent value="modules">
            <div className="space-y-4">
              {/* Dissertation */}
              <Card className="card-sjh">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#1A365D] to-[#2D4A6F] rounded-lg">
                      <PenTool className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Dissertation
                      </h3>
                      <p className="text-[#64748B] mb-4">
                        Entrainez-vous a la dissertation avec des encadres separes pour chaque partie.
                      </p>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Introduction structuree (accroche, analyse, problematique)
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          These / Antithese / Synthese
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Pistes de reflexion generees par l'IA
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Evaluation detaillee avec note sur 20
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commentaire */}
              <Card className="card-sjh">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Commentaire de texte
                      </h3>
                      <p className="text-[#64748B] mb-4">
                        Analysez les grands textes philosophiques avec le texte complet affiche.
                      </p>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Texte integral affiche en permanence
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Parties I, II, III separees
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Conseils methodologiques integres
                        </div>
                        <div className="flex items-center gap-2 text-[#64748B]">
                          <Check className="w-4 h-4 text-[#22C55E]" />
                          Discussion critique guidee
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Thématiques */}
              <Card className="card-sjh">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#EC4899] to-[#DB2777] rounded-lg">
                      <GraduationCap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thématiques Bac
                      </h3>
                      <p className="text-[#64748B] mb-4">
                        Explorez les 17 notions du programme et decouvrez ce qu'en pensent les philosophes.
                      </p>
                      <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-0">
                        Ideal pour construire these/antithese
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bibliothèque */}
              <Card className="card-sjh">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-lg">
                      <Library className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Bibliotheque des Philosophes
                      </h3>
                      <p className="text-[#64748B] mb-4">
                        Fiches detaillees sur les 21 philosophes avec leurs oeuvres et positions.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">Biographies</Badge>
                        <Badge variant="outline">Oeuvres analysees</Badge>
                        <Badge variant="outline">Themes du Bac</Badge>
                        <Badge variant="outline">Discuter avec eux</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quiz */}
              <Card className="card-sjh">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gradient-to-br from-[#EF4444] to-[#DC2626] rounded-lg">
                      <HelpCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Quiz de Remediation
                      </h3>
                      <p className="text-[#64748B] mb-4">
                        Testez vos connaissances sur les définitions et concepts de base.
                      </p>
                      <Badge className="bg-[#EF4444]/20 text-[#EF4444] border-0">
                        Réponses basées sur les erreurs courantes
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Chat & Debate */}
          <TabsContent value="chat">
            <div className="space-y-4">
              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <MessageCircle className="w-6 h-6 text-[#3B82F6]" />
                    Chat Prive
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#64748B]">
                    Discutez en tete-a-tete avec n'importe quel philosophe. Posez vos questions, demandez des explications, explorez les concepts !
                  </p>
                  <div className="p-4 bg-[#3B82F6]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Exemples de questions :</h4>
                    <ul className="space-y-2 text-sm text-[#64748B]">
                      <li>"Descartes, expliquez-moi le cogito"</li>
                      <li>"Nietzsche, que pensez-vous de la morale chretienne ?"</li>
                      <li>"Saint Thomas, comment prouver l'existence de Dieu ?"</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <Users className="w-6 h-6 text-[#22C55E]" />
                    Agora Philosophique
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#64748B]">
                    Chat de groupe avec votre classe et les philosophes. Debattez ensemble sur les grandes questions !
                  </p>
                  <Badge className="bg-[#22C55E]/20 text-[#22C55E] border-0">
                    Les philosophes peuvent se repondre entre eux
                  </Badge>
                </CardContent>
              </Card>

              <Card className="card-sjh">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <Target className="w-6 h-6 text-[#8B5CF6]" />
                    Discuter d'un sujet
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#64748B]">
                    Proposez un theme (ex: "La liberte est-elle une illusion ?") et 3 philosophes sont suggeres pour en debattre. 
                    Observez leurs arguments s'affronter !
                  </p>
                  <div className="p-4 bg-[#8B5CF6]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Comment ca marche :</h4>
                    <ol className="space-y-2 text-sm text-[#64748B] list-decimal list-inside">
                      <li>Entrez votre sujet de reflexion</li>
                      <li>SophIA suggere 3 philosophes pertinents</li>
                      <li>Chaque philosophe fait une declaration d'ouverture</li>
                      <li>Posez vos questions pour approfondir le debat</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Exercises */}
          <TabsContent value="exercises">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <PenTool className="w-6 h-6 text-[#22C55E]" />
                  Bien rediger vos exercices
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Dissertation tips */}
                <div>
                  <h3 className="font-semibold text-[#1A365D] mb-3">Dissertation</h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <h4 className="font-medium text-[#1A365D] mb-1">Introduction</h4>
                      <p className="text-sm text-[#64748B]">
                        Commencez par une accroche (exemple concret, citation). Analysez les termes du sujet. 
                        Formulez une problematique sous forme de question. Annoncez votre plan.
                      </p>
                    </div>
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <h4 className="font-medium text-[#1A365D] mb-1">Developpement (3 parties)</h4>
                      <p className="text-sm text-[#64748B]">
                        I. These - Defendez une premiere reponse avec 2-3 arguments et exemples<br/>
                        II. Antithese - Montrez les limites et defendez la position inverse<br/>
                        III. Synthese - Depassez l'opposition, nuancez, enrichissez
                      </p>
                    </div>
                    <div className="p-3 bg-[#F8F9FA] rounded-lg">
                      <h4 className="font-medium text-[#1A365D] mb-1">Conclusion</h4>
                      <p className="text-sm text-[#64748B]">
                        Resumez votre parcours argumentatif. Donnez votre reponse finale a la problematique. 
                        Terminez par une ouverture vers une question connexe.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Commentary tips */}
                <div>
                  <h3 className="font-semibold text-[#1A365D] mb-3">Commentaire de texte</h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-[#8B5CF6]/10 rounded-lg">
                      <h4 className="font-medium text-[#1A365D] mb-1">Avant de rédiger</h4>
                      <p className="text-sm text-[#64748B]">
                        Lisez le texte 3 fois. Identifiez la thèse principale. Repérez les étapes de l'argumentation. 
                        Soulignez les concepts clés.
                      </p>
                    </div>
                    <div className="p-3 bg-[#8B5CF6]/10 rounded-lg">
                      <h4 className="font-medium text-[#1A365D] mb-1">Structure du commentaire</h4>
                      <p className="text-sm text-[#64748B]">
                        Suivez le mouvement du texte (lineaire) OU regroupez par themes (thematique). 
                        Chaque partie doit expliquer ET analyser une portion du texte.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tips */}
          <TabsContent value="tips">
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Lightbulb className="w-6 h-6 text-[#F59E0B]" />
                  Conseils pour le Baccalaureat
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#22C55E]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Les 17 notions</h4>
                    <p className="text-sm text-[#64748B] mb-2">
                      Maîtrisez au moins 3 philosophes par notion. Utilisez la page "Thématiques Bac" pour trouver leurs positions.
                    </p>
                    <Badge className="bg-[#22C55E]/20 text-[#22C55E] border-0 text-xs">
                      Conseil: Choisissez 1 Ancien, 1 Moderne, 1 Contemporain
                    </Badge>
                  </div>

                  <div className="p-4 bg-[#3B82F6]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Citations utiles</h4>
                    <p className="text-sm text-[#64748B] mb-2">
                      Apprenez 2-3 citations par philosophe. Utilisez la Bibliotheque pour les trouver dans leur contexte.
                    </p>
                    <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] border-0 text-xs">
                      Astuce: Notez les citations qui vous parlent
                    </Badge>
                  </div>

                  <div className="p-4 bg-[#8B5CF6]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Entrainement regulier</h4>
                    <p className="text-sm text-[#64748B] mb-2">
                      Faites au moins 1 exercice par semaine. Alternez dissertation et commentaire.
                    </p>
                    <Badge className="bg-[#8B5CF6]/20 text-[#8B5CF6] border-0 text-xs">
                      Objectif: 4h d'entrainement = 4h d'epreuve
                    </Badge>
                  </div>

                  <div className="p-4 bg-[#F59E0B]/10 rounded-lg">
                    <h4 className="font-semibold text-[#1A365D] mb-2">Le jour J</h4>
                    <p className="text-sm text-[#64748B] mb-2">
                      Lisez tous les sujets. Prenez 10 min pour choisir. Faites un brouillon detaille avant de rediger.
                    </p>
                    <Badge className="bg-[#F59E0B]/20 text-[#F59E0B] border-0 text-xs">
                      Gestion du temps: 1h intro/plan, 2h30 dev, 30min conclu/relecture
                    </Badge>
                  </div>
                </div>

                <div className="p-4 bg-[#1A365D]/5 rounded-lg border border-[#1A365D]/20">
                  <h4 className="font-semibold text-[#1A365D] mb-2">
                    SophIA vous accompagne jusqu'au Bac !
                  </h4>
                  <p className="text-sm text-[#64748B]">
                    N'hesitez pas a poser des questions aux philosophes, meme les plus simples. 
                    Chaque conversation vous fait progresser et vous rapproche de la reussite.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate("/dashboard")}
            className="bg-[#1A365D] hover:bg-[#132745]"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Button>
        </div>
      </main>
    </div>
  );
};

export default UserManualPage;
